import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { ICON_PATHS, IconType } from './Icon.types';
import { withAssetPrefix } from '@/lib/assetPrefix';

export { ICON_PATHS, IconType };

interface IconProps {
  src: IconType | string;
  size?: number;
  className?: string;
  /**
   * The icon's accessible name, for the rare icon that carries meaning no
   * visible text repeats — an icon-only control, or a term whose glyph *is*
   * the label (the pin, calendar and clock in a booking confirmation).
   *
   * Leave it out and the icon stays `aria-hidden`, which is the right answer
   * for the large majority: an icon beside its own label is decoration, and
   * naming it reads the label out twice. See
   * [70.14-Image-Alt-Text](../../../spec/70-Standards/70.14-Image-Alt-Text.md)
   * § Icons.
   *
   * Pass editor-typed text through `sub()` before it gets here — a literal
   * `{domain.aktion}` in an accessible name is read out verbatim.
   *
   * Note that a bare `aria-label` on `<Icon>` does *not* work: TypeScript
   * waves hyphenated JSX attributes through unchecked, and this component
   * spreads nothing, so such a prop is silently dropped. That trap is the
   * reason this prop exists.
   */
  label?: string;
}

export const Icon = ({ src, size = 24, className, label }: IconProps) => {
  const resolvedSrc = withAssetPrefix(ICON_PATHS[src] ?? src);
  // Trimmed, because a blank name is not a name: an icon labelled with an
  // empty editor field would otherwise be announced as an unnamed image,
  // which is worse than staying silent. Same rule as `imageAlt()`.
  const name = label?.trim();
  return (
    <span
      // A named icon is exposed as an image with that name; an unnamed one is
      // hidden outright, so a screen reader never announces a bare "image".
      {...(name
        ? { 'role': 'img', 'aria-label': name }
        : { 'aria-hidden': 'true' as const })}
      className={classNames('inline-block bg-current', className)}
      style={{
        width: size,
        height: size,
        mask: `url(${resolvedSrc}) no-repeat center / contain`,
        WebkitMask: `url(${resolvedSrc}) no-repeat center / contain`,
      }}
    />
  );
};
